import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Target, TrendingUp, Zap, X, Sparkles, Star, Shield, CheckCircle } from "lucide-react";
import storycraftLogo from "@/assets/storycraft-logo-new.png";
import ipadMockup from "@/assets/ipad-mockup-delivered.png";
import ipadMockupSquare from "@/assets/ipad-mockup-square-2.png";
import avatarIsaac from "@/assets/avatar-james.png";

/* ─── Count-up hook ─── */
function useCountUp(target: number, duration = 1500, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration, start]);
  return value;
}

/* ─── Scroll-reveal hook ─── */
function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ─── Data ─── */
const benefits = [
  { icon: Target, title: "Personalised Roadmap", desc: "A step-by-step plan tailored to your niche, audience size, and goals" },
  { icon: TrendingUp, title: "Revenue Milestones", desc: "Specific income targets with realistic timelines based on your situation" },
  { icon: Zap, title: "Your Next 3 Actions", desc: "Exact steps you can start today — no guesswork, no fluff" },
  { icon: CheckCircle, title: "Product Recommendations", desc: "Which digital products fit your audience and how to price them" },
];

const marqueeItems = [
  "✦ AI-Personalised Plans",
  "✦ Built by 500+ Creators",
  "✦ 3-Minute Assessment",
  "✦ Free Report",
  "✦ No Fluff — Just Your Plan",
  "✦ Revenue Targets Included",
];

/* ─── Counter stat ─── */
function CounterStat({ target, suffix, label }: { target: number; suffix: string; label: string }) {
  const { ref, visible } = useReveal(0.2);
  const count = useCountUp(target, 1500, visible);
  return (
    <div ref={ref} className="text-center">
      <p className="font-display text-3xl md:text-4xl font-bold text-primary">
        {count}{suffix}
      </p>
      <p className="text-xs md:text-sm mt-1 text-muted-foreground">{label}</p>
    </div>
  );
}

/* ─── Mobile sticky CTA ─── */
function MobileStickyQuizCTA() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const h = () => setVisible(window.scrollY > window.innerHeight * 0.25);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  if (dismissed || !visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden p-3 flex items-center gap-2 bg-white/95 backdrop-blur-lg border-t border-border">
      <Link to="/quiz" className="flex-1">
        <button className="w-full group text-sm min-h-[48px] rounded-xl font-bold text-primary-foreground bg-primary flex items-center justify-center gap-2 btn-interactive">
          Build My Free Roadmap
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </Link>
      <button onClick={() => setDismissed(true)} className="p-2 text-muted-foreground" aria-label="Dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

/* ─── Main Page ─── */
export default function QuizHome() {
  return (
    <div className="min-h-screen bg-primary" style={{ color: "#FFFFFF" }}>
      <Helmet>
        <title>Free Creator Monetisation Quiz | AI StoryCraft Studio</title>
        <meta name="description" content="Answer 11 quick questions and get your personalised creator monetisation roadmap. AI-powered, tailored to your niche. Free in under 3 minutes." />
        <meta property="og:title" content="Free Creator Monetisation Quiz | AI StoryCraft Studio" />
        <meta property="og:description" content="Answer 11 quick questions and get your personalised creator monetisation roadmap. AI-powered, tailored to your niche. Free in under 3 minutes." />
        <meta property="og:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
        <link rel="canonical" href="https://ai-storycraft-studio.com/quiz-home" />
      </Helmet>
      <MobileStickyQuizCTA />

      {/* ═══ HERO — Testimonial style ═══ */}
      <section className="pt-12 md:pt-20 pb-10 md:pb-16 text-center px-4 md:px-6" style={{ background: "hsl(var(--primary))" }}>
        {/* Avatar + name */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative mb-3">
            <img src={avatarIsaac} alt="Creator testimonial" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white flex items-center justify-center">
              <img src={storycraftLogo} alt="" className="w-5 h-5 rounded object-contain" />
            </div>
          </div>
          <p className="text-sm font-semibold text-white">James, YouTube Creator</p>
          <p className="text-xs text-white/60">12K subscribers</p>
        </div>

        {/* Big testimonial quote */}
        <h1 className="text-[24px] md:text-[44px] font-extrabold leading-[1.12] tracking-tight max-w-3xl mx-auto mb-5" style={{ color: "#FFFFFF" }}>
          "This quiz gave me a clear roadmap to{" "}
          <span className="text-accent">monetise my audience</span>{" "}
          — I made my first sale within 2 weeks"
        </h1>

        <p className="text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed text-white/70">
          Join 500+ creators who've used this free AI-powered assessment to get a personalised monetisation plan — tailored to their niche, audience, and goals.
        </p>
      </section>

      {/* ═══ TWO-COLUMN: Mockup + CTA Card ═══ */}
      <section className="px-4 md:px-6 -mt-2 pb-10 md:pb-16" style={{ background: "hsl(var(--primary))" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-stretch">
          {/* Left: Report mockup */}
          <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center order-2 md:order-1">
            <img
              src={ipadMockup}
              alt="Preview of your personalised creator monetisation report"
              className="w-full h-full object-cover rounded-2xl"
            />
          </div>

          {/* Right: CTA card (dark accent panel) */}
          <div className="rounded-2xl p-6 md:p-8 flex flex-col justify-center items-center text-center order-1 md:order-2" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-4 bg-white/10 text-white/80 border border-white/10">
              <Sparkles className="w-3.5 h-3.5" />
              Free AI-Powered Assessment
            </div>

            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
              Get Your Free Monetisation Roadmap
            </h2>
            <p className="text-sm text-white/60 mb-6 max-w-sm">
              Answer 11 quick questions. Get your personalised plan showing exactly how to turn your content into income.
            </p>

            <Link to="/quiz" className="w-full max-w-xs">
              <button className="w-full group inline-flex items-center justify-center gap-2.5 text-base md:text-lg px-8 min-h-[52px] md:min-h-[56px] rounded-xl font-bold text-primary bg-white shadow-lg transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] quiz-cta-pulse cursor-pointer">
                Build My Free Roadmap
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>

            <div className="flex items-center gap-1.5 mt-4">
              <Shield className="w-3.5 h-3.5 text-white/40" />
              <p className="text-xs text-white/40">
                Takes 3 minutes · 100% free · No signup required
              </p>
            </div>

            {/* Star rating */}
            <div className="flex flex-col items-center mt-6 pt-5 border-t border-white/10 w-full">
              <div className="flex items-center gap-0.5 mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-xs text-white/50">4.9 from 500+ creators</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ MARQUEE — on white bg ═══ */}
      <div className="overflow-hidden py-4 bg-background border-b border-border">
        <div className="quiz-marquee-track">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className="inline-flex items-center px-5 text-sm font-medium whitespace-nowrap text-muted-foreground" style={{ fontSize: "clamp(13px, 1.2vw, 14px)" }}>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ═══ REST OF PAGE — Light background ═══ */}
      <div className="bg-background" style={{ color: "hsl(220 9% 12%)" }}>

        {/* ═══ COUNTER STATS ═══ */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-3 gap-4">
            <CounterStat target={500} suffix="+" label="Creators" />
            <CounterStat target={3} suffix=" min" label="To Complete" />
            <CounterStat target={11} suffix="" label="Questions" />
          </div>
        </section>

        {/* ═══ BENEFITS ═══ */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Reveal>
            <p className="text-center text-xs uppercase tracking-widest font-medium text-primary mb-3">
              What's inside your free report
            </p>
            <h2 className="text-center font-bold mb-10 text-foreground" style={{ fontSize: "clamp(22px, 3vw, 32px)" }}>
              Here is a little insight into what you can expect:
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {benefits.map((b, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-card rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-default border border-border text-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto bg-primary/10">
                    <b.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-base font-bold mb-1.5 text-foreground">{b.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* ═══ REPORT MOCKUP FULL-WIDTH ═══ */}
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-border bg-card">
              <img
                src={ipadMockupSquare}
                alt="Full preview of your personalised creator monetisation report"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </Reveal>
        </section>

        {/* ═══ FOUNDER SECTION ═══ */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <Reveal>
            <p className="text-xs uppercase tracking-widest font-medium text-primary mb-3">
              About us and our mission for Creators
            </p>
            <h2 className="font-bold mb-6 text-foreground" style={{ fontSize: "clamp(22px, 3vw, 32px)" }}>
              Who are we to make these promises to you?
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed">
              <p>
                We built AI StoryCraft Studio because we saw too many talented creators stuck — posting consistently but never making money from their content.
              </p>
              <p>
                After helping 500+ creators launch their first digital products, we noticed the same patterns. The ones who succeeded had a clear roadmap. The ones who didn't were guessing.
              </p>
              <p>
                That's why we built this free quiz — to give every creator, regardless of audience size, a personalised plan they can act on today.
              </p>
              <p>
                Let us show you exactly what to build, how to price it, and when to launch it.
              </p>
              <p className="font-medium text-foreground">Best regards</p>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="flex items-center gap-3 mt-6">
              <img src={avatarIsaac} alt="Isaac" className="w-12 h-12 rounded-full object-cover" />
              <div>
                <p className="text-sm font-semibold text-foreground">Isaac</p>
                <p className="text-xs text-muted-foreground">Founder, AI StoryCraft Studio</p>
              </div>
            </div>
          </Reveal>
        </section>

        {/* ═══ FINAL CTA ═══ */}
        <section className="py-12 md:py-16 px-4 md:px-6" style={{ background: "hsl(var(--primary))" }}>
          <div className="max-w-xl mx-auto text-center">
            {/* Stars */}
            <div className="flex items-center justify-center gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-accent text-accent" />
              ))}
            </div>
            <p className="text-xs text-white/50 mb-6">4.9 from 500+ creators</p>

            <p className="text-sm md:text-base text-white/80 mb-6 max-w-md mx-auto">
              Get your <strong className="text-white">free personalised roadmap</strong> now and receive your exact monetisation strategy tailored to <strong className="text-white">your niche and audience</strong>:
            </p>

            <Link to="/quiz">
              <button className="group inline-flex items-center justify-center gap-2.5 text-base md:text-lg px-10 min-h-[52px] md:min-h-[56px] rounded-xl font-bold text-primary bg-white shadow-lg transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] quiz-cta-pulse cursor-pointer">
                Build My Free Roadmap
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>

            <div className="flex items-center justify-center gap-1.5 mt-4">
              <Shield className="w-3.5 h-3.5 text-white/40" />
              <p className="text-xs text-white/40">Free · No signup · Takes 3 minutes</p>
            </div>
          </div>
        </section>

        {/* ═══ FOOTER ═══ */}
        <footer className="py-8 bg-background border-t border-border">
          <div className="max-w-5xl mx-auto px-4 md:px-6 flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={storycraftLogo} alt="AI StoryCraft Studio" className="w-6 h-6 rounded-md object-contain" />
              <span className="text-xs font-medium text-muted-foreground">Powered by AI StoryCraft Studio</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
